import {Authentication} from "./Authentication";
import {AuthButton} from "./AuthButton";
import {Project} from "./Project";
import {SelectInput} from "../utils/ui/SelectInput";
import {ConversionUtils} from "../utils/ConversionUtils";
import {Collection} from "../utils/Collection";
import {Main} from "../Main";
import {ConfirmWindow} from "../ui/ConfirmWindow";
import {BrowserUtils} from "utils/BrowserUtils";
import {StartTemplates} from "../ui/StartTemplates";
import {XHRLoader} from "../utils/XHRLoader";
import {WarningWindow} from "../ui/WarningWindow";

const startDiv = document.getElementById("startdiv");

export class AuthenticationView {
    private _authentication: Authentication;

    private static PLEASE_SIGN_IN: string = "Please sign in to your Mueller account and then submit for a quote.";

    private _loginStatus: HTMLSpanElement;
    private _logIn: AuthButton;

    private _saveBtn: AuthButton;
    private _saveAsBtn: AuthButton;
    private _deleteBtn: AuthButton;
    private _specSheetBtn: AuthButton;
    private _startOverBtn: AuthButton;

    private _projectSelect: SelectInput<Project>;

    // these could belong to an encapsulated view as they're not strictly
    // part of the authentication
    private _getQuoteBtn: AuthButton;

    constructor(authentication: Authentication) {
        this._authentication = authentication;

        this._loginStatus = <HTMLSpanElement>document.getElementById("loginStatus");
        this._logIn = new AuthButton("login");
        this._logIn.click.add(this.onLogInClicked);

        this._saveBtn = new AuthButton("saveProject");
        this._saveBtn.click.add(this.onSaveClick);

        this._saveAsBtn = new AuthButton("saveProjectAs");
        this._saveAsBtn.click.add(this.onSaveAsClick);

        this._deleteBtn = new AuthButton("deleteProject");
        this._deleteBtn.click.add(this.onDeleteClick);

        this._specSheetBtn = new AuthButton("spec_sheet");
        this._specSheetBtn.click.add(this.specSheetClick);

        this._startOverBtn = new AuthButton("start_over");
        this._startOverBtn.click.add(this.startOverClick);

        this._projectSelect = new SelectInput<Project>({
            element: <HTMLSelectElement>document.getElementById("projectSelect"),
            labelProperty: "name",
            values: this._authentication.projects
        });
        this._projectSelect.changeSignal.add(this.onProjectSelect);

        // get quote
        this._getQuoteBtn = new AuthButton("getQuoteWrapper");
        this._getQuoteBtn.click.add(this.onGetQuoteClick);

        this.hideLoginDependantButtons();

        this._authentication.loginChange.add(this.onChangeLogin);
        this._authentication.projectsLoaded.add(this.onProjectsLoaded);
        this._authentication.selectedProjectChanged.add(this.onSelectedProjectChanged);

        this.tryToRetrieveProject();
    }

    // ======================================================================================================
    // get quote

    public onGetQuoteClick = async () => {
        // user needs to be logged in and have a saved project to be able to get a quote

        if (!this._authentication.loggedIn) {
            const confirmed = await ConfirmWindow.open(AuthenticationView.PLEASE_SIGN_IN, "Sign In", "Cancel");
            if (confirmed) {
                this.onLogInClicked();
            }
        } else {
            const projectInfo = Authentication.getProjectInfoFromURL();

            if (projectInfo === null) {
                await this.unsavedProjectWarning();
            }
            else{
                this.checkDiscontinuedColors(projectInfo);
            }
        }
    };

    public checkDiscontinuedColors(projectInfo) {

        var xhr = XHRLoader.load({
            url: Authentication.URL + "get_project.php",
            method: XHRLoader.METHOD_GET,
            params: {
                email: projectInfo.email,
                project_name: projectInfo.project
            },
            onComplete: this.getProjectSuccess,
            onFail: this.projectFail
        });
    }

    private getProjectSuccess = async (xhr, result, params) => {

        let colorChangeConfirmed = true;

        if (result.warning !== "") {
            colorChangeConfirmed = false;
            await ConfirmWindow.open(result.warning, "OK", "Cancel");
        }

        if (colorChangeConfirmed) {
            const selectedProjectName = this._authentication.selectedProject.name;
            if (this._authentication.isSelectedProjectInitial()) {

                const confirmed = ConfirmWindow.open("Please first save your project to be able to submit it for a quote.", "Save", "Cancel");
                if (confirmed) {
                    this.onSaveClick();
                }
            } else {
                console.log("end")
                // save project
                this._authentication.onSave();
                /**
                 * The project name is stored without the (QUOTED) part in the database. The button redirects to https://apps.muellerinc.com/my-account2 where the php code uses the project name to get the project information.
                 */
                let projectName = selectedProjectName.replace(" (QUOTED)", "")

                const queryString = ConversionUtils.buildQueryString(
                    [
                        "email", this._authentication.email,
                        "project", projectName,
                    ],
                    "&"
                );

                // default is apps
                let url = "https://apps.muellerinc.com/my-account2/?requestquote=yes" + queryString;

                if(Authentication.getPHPPath().includes("apps-prd")){
                    url = "https://apps-prd.muellerinc.com/my-account2/?requestquote=yes" + queryString;
                }
                else if(Authentication.getPHPPath().includes("apps-dev")){
                    url = "https://apps-dev.muellerinc.com/my-account2/?requestquote=yes" + queryString;
                }

                window.location.href = url;
            }
        }

    }

    private projectFail = async (xhr, result, params) => {
        const confirmed = ConfirmWindow.open("Please first save your project to be able to submit it for a quote.", "Save", "Cancel");
        if (confirmed) {
            await this.onSaveClick();
        }
    }

    private unsavedProjectWarning = async () => {
        if (await ConfirmWindow.open("Please first save your project to be able to submit it for a quote.", "Save", "Cancel")) {
            await this.onSaveClick();
        }
    }

    // ======================================================================================================
    // Login, registration

    private onLogInClicked = async () => {
        if (this._authentication.loggedIn) {
            // logout
            if (await ConfirmWindow.open(`Are you sure you want to log out?`)) {
                localStorage.clear();
                this._authentication.checkSamlSession();

            }
        } else {
            this._authentication.saveProjectToLocalStorage();
            AuthenticationView.redirectToLogin();
        }
    };

    public static readonly redirectToLogin = () => {
        const currentBaseURL = BrowserUtils.getCurrentBaseURL();
        // default is apps
        let url = `https://apps.muellerinc.com/samlapp/l.php?source=${currentBaseURL}`;

        if(Authentication.getPHPPath().includes("apps-prd")){
            url = `https://apps-prd.muellerinc.com/samlapp/l.php?source=${currentBaseURL}`;
        }
        else if(Authentication.getPHPPath().includes("apps-dev")){
            url = `https://apps-dev.muellerinc.com/samlapp/l.php?source=${currentBaseURL}`;
        }

        window.open(url, "_self");
    };

    private async tryToRetrieveProject() {
        const savedProject = this._authentication.getProjectFromLocalStorage();
        if (savedProject) {
            const configObject = JSON.parse(savedProject);
            const areTheyTheSame = this._authentication.doesCurrentConfigEqualsInitial(configObject);

            if (!areTheyTheSame) {
                const email = this._authentication.getProjectEmailFromLocalStorage();
                const projectName = this._authentication.getProjectNameFromLocalStorage();
                if (email !== "null" && projectName !== "null") {
                    const confirm = await ConfirmWindow.open(`An opened project was detected. Would you like to reload it now?`);
                    if (confirm) {
                        location.href = location.origin + location.pathname + `/?email=${email}&project=${projectName}`;
                    }
                } else {
                    const confirm = await ConfirmWindow.open(`An unsaved project was detected. Would you like to reload it now?`);
                    if (confirm) {
                        Main.getInstance().setProjectSettings(configObject);
                        startDiv?.classList.add("hidden");
                    }
                }
            }
        }
    }

    private showLoginDependantButtons() {
        document.getElementById("pleaseLogIn").style.display = "none";
        this._projectSelect.show();
        this._saveBtn.show();
        this._saveAsBtn.show();
        this._deleteBtn.show();
        this._specSheetBtn.show();
        this._getQuoteBtn.show();
        document.getElementById("sharePanelToggler").style.display = "";
    }

    private hideLoginDependantButtons() {
        document.getElementById("pleaseLogIn").style.display = "";
        this._projectSelect.hide();
        this._saveBtn.hide();
        this._saveAsBtn.hide();
        this._deleteBtn.hide();
        this._specSheetBtn.hide();
        this._getQuoteBtn.hide();
        document.getElementById("sharePanelToggler").style.display = "none";
    }

    private onChangeLogin = (userName: string) => {
        if (userName) {
            this._loginStatus.innerHTML = `${userName}`;
            this._logIn.setToLogOut();
            this.showLoginDependantButtons();
            Main.getInstance().onResize();

            startDiv?.querySelector(".signin")?.classList.add("hidden");
        } else {
            // log out

            this._loginStatus.textContent = "You're not logged in.";
            this._logIn.setToLogIn();

            this._saveAsBtn.disable();
            this._deleteBtn.disable();

            this.hideLoginDependantButtons();
        }
    };

    // ======================================================================================================
    // Projects

    private onSaveClick = async () => {
        if (!this._authentication.loggedIn) {
            const confirmed = await ConfirmWindow.open(AuthenticationView.PLEASE_SIGN_IN, "Sign In", "Cancel");
            if (confirmed) {
                this.onLogInClicked();
            }
        } else {
            this._authentication.onSave();
        }
    };

    private onSaveAsClick = () => {
        this._authentication.onSaveAs();
    };

    private onDeleteClick = () => {
        this._authentication.onDelete();
    };

    private specSheetClick = async () => {
        if (!this._authentication.loggedIn) {
            const confirmed = await ConfirmWindow.open(AuthenticationView.PLEASE_SIGN_IN, "Sign In", "Cancel");
            if (confirmed) {
                this.onLogInClicked();
            }
        } else {
            if (this._authentication.isSelectedProjectSaved() || this._authentication.selectedProject.isQuoted) {
                this.showSpec();
            } else {
                if (await ConfirmWindow.open("Please save your project to view the spec sheet.", "Save", "Cancel")) {
                    await this._authentication.onSave();
                }
            }
        }
    };

    private startOverClick = async () => {
        await this._authentication.selectProjectByName("Unsaved project");
        const startTemplates = new StartTemplates();
    }

    private showSpec() {
        const projname = decodeURIComponent(window.location.search); // "%40" -> "@"
        const url = `${BrowserUtils.getCurrentBaseURL()}php/specsheet3d.php${projname}`;
        const win = window.open(url, "_blank");
        win.focus();
    };

    private onProjectsLoaded = (projects: Collection<Project>) => {
        this._projectSelect.setValues(projects);
    };

    // user selected a different project
    private onProjectSelect = (projectName: string) => {
        this._authentication.selectProjectByName(projectName);
    };

    // user selected a project, or initiated from Authentication
    // triggered:
    // - after a new project is created (and so selected)
    private onSelectedProjectChanged = (project: Project) => {
        this._projectSelect.value = project;

        if (this._authentication.isSelectedProjectInitial()) {
            this._saveAsBtn.disable();
            this._deleteBtn.disable();
        } else {
            this._saveAsBtn.enable();
            this._deleteBtn.enable();
        }
    };

    public get projectSelect() {
        return this._projectSelect;
    }

    public disableAll = () => {
        this._saveAsBtn.disable();
        this._deleteBtn.disable();
    };

    public updateButtons = () => {
        if (this._authentication.loggedIn) {
            if (!this._authentication.isSelectedProjectInitial()) {
                this._deleteBtn.enable();
                this._saveAsBtn.enable();
            }
        }
    };
}
